import { Container, Skeleton } from 'components/common';
import HeaderSEO from 'components/HeaderSEO';
import { GetServerSideProps } from 'next';
import { ProductCategory } from 'lib/productCategory';
import { CollectionMenu } from 'lib/sellingProduct';
import { useMemo } from 'react';
import { IntroBanner, MealActionBar, useHomePage } from 'lib/home';
import dynamic from 'next/dynamic';
import { isTruthy } from 'lib/core';

const SocialProof = dynamic(import('lib/home/SocialProof'), {
  ssr: false,
  loading: () => (
    <Container className="tw-pt-6">
      <Skeleton rectWidth="100%" options={{ numberOfLines: 3 }} />
    </Container>
  ),
});

const HowDoesYummyBrosWork = dynamic(
  import('lib/home/HowDoesYummyBrosWork'),
  { ssr: false },
);

const CategoriesAndCollections = dynamic(
  import('lib/home/CategoriesAndCollections'),
  { ssr: false },
);

const PerksSection = dynamic(import('lib/home/PerksSection'), {
  ssr: false,
});

const EarningsSection = dynamic(import('lib/home/EarningsSection'), {
  ssr: false,
});

const ProductsSection = dynamic(import('lib/home/ProductsSection'), {
  ssr: false,
});

const SpecialOfferings = dynamic(
  import('components/SpecialOfferings'),
  {
    ssr: false,
  },
);

const HomePage = ({
  categories,
  collections,
  cateringEnabled,
  ybExpressEnabled,
}: {
  categories: ProductCategory[];
  collections: CollectionMenu[];
  cateringEnabled: boolean;
  ybExpressEnabled: boolean;
}) => {
  const { isAuthLoading, user } = useHomePage();

  const hasSession = useMemo(() => Boolean(user), [user]);

  return (
    <>
      <HeaderSEO
        title="Yummy Bros - The Most Enjoyable Healthy Meal Prep in Singapore!"
        description="With Yummy Bros highly-rated meal prep, you'll have high protein, customizable, & healthy food delivered straight to your doorstep! Order online today!"
        path="https://www.yummybros.com"
        image="https://yb-production.s3.ap-southeast-1.amazonaws.com/images/static/facebook-share-image-2020.jpeg"
        ogType="website"
        includeBusinessMarkup
      />
      <IntroBanner />

      {isAuthLoading ? (
        <div className="tw-flex tw-w-full tw-justify-center tw-items-center tw-py-8">
          <Container>
            <Skeleton
              type="line"
              rectWidth="100%"
              options={{ numberOfLines: 1, lineHeight: 120 }}
            />
          </Container>
        </div>
      ) : hasSession ? (
        <div className="tw-flex tw-flex-col tw-my-16 lg:tw-my-14 tw-space-y-8 lg:tw-space-y-16">
          <PerksSection />
          <EarningsSection />
          <ProductsSection
            categories={categories}
            collections={collections}
            cateringEnabled={cateringEnabled}
            ybExpressEnabled={ybExpressEnabled}
          />
        </div>
      ) : (
        <>
          <SocialProof />
          <HowDoesYummyBrosWork />
          <CategoriesAndCollections
            categories={categories}
            collections={collections}
            squareOnMobile
          />
          <SpecialOfferings
            title="What else does Yummy Bros offer?"
            cateringEnabled={cateringEnabled}
            ybExpressEnabled={ybExpressEnabled}
          />
        </>
      )}
      <MealActionBar />
    </>
  );
};

export default HomePage;

export const getServerSideProps: GetServerSideProps<{
  categories: ProductCategory[];
  collections: CollectionMenu[];
}> = async (context) => {
  let _collections: CollectionMenu[] = [];

  const newLaunchesCollections =
    process.env.NEW_LAUNCHES_COLLECTION_ID;
  const hotSellerCollections = process.env.HOT_SELLERS_COLLECTION_ID;

  try {
    if (newLaunchesCollections)
      _collections.push(JSON.parse(newLaunchesCollections));

    if (hotSellerCollections)
      _collections.push(JSON.parse(hotSellerCollections));

    const categories: ProductCategory[] = process.env.CATEGORIES
      ? JSON.parse(process.env.CATEGORIES)
      : [];

    const cateringEnabled = isTruthy(process.env.CATERING_ENABLED);
    const ybExpressEnabled = isTruthy(process.env.YB_EXPRESS_ENABLED);

    return {
      props: {
        categories,
        collections: _collections,
        cateringEnabled,
        ybExpressEnabled,
      },
    };
  } catch {
    return {
      props: {
        categories: [],
        collections: _collections,
        cateringEnabled: false,
        ybExpressEnabled: false,
      },
    };
  }
};
